import { apiClient } from "./client";

/**
 * Magazzino (Fase 4 punto 8). Due risorse indipendenti sotto lo stesso
 * prefisso: gli ORDINI ai fornitori e i pezzi di consumo DISPONIBILI.
 *
 */

export type StatoOrdineMagazzino = "da_confermare" | "confermato";
export type StatoMatchCliente = "nessuno" | "automatico" | "da_confermare" | "risolto_manualmente";
export type TipoAccessorio = "sacco_cassaforte" | "cassetto_cassaforte" | "accettatore_banconote";
export type UbicazioneMagazzino =
  | "presso_fornitore"
  | "nostro_magazzino"
  | "centro_assistenza"
  | "presso_cliente";

export const STATO_ORDINE_LABELS: Record<StatoOrdineMagazzino, string> = {
  da_confermare: "Da confermare",
  confermato: "Confermato",
};

/**
 * Due valori dicono entrambi "il cliente c'e'", ma per strade diverse, e le
 * etichette lo rispettano: «Agganciato» l'ha fatto il sistema confrontando la
 * P.IVA, «Assegnato a mano» l'ha deciso una persona.
 */
export const STATO_MATCH_LABELS: Record<StatoMatchCliente, string> = {
  nessuno: "Nessun cliente",
  automatico: "Agganciato",
  da_confermare: "Da confermare",
  risolto_manualmente: "Assegnato a mano",
};

export const UBICAZIONE_LABELS: Record<UbicazioneMagazzino, string> = {
  presso_fornitore: "Presso il fornitore",
  nostro_magazzino: "Nostro magazzino",
  centro_assistenza: "Centro assistenza",
  presso_cliente: "Consegnata al cliente",
};

/**
 * Le tre ubicazioni che contano come merce nostra. `presso_cliente` sta fuori:
 * è uscita, e sommarla al disponibile direbbe di avere pezzi che non si hanno.
 */
export const UBICAZIONI_DISPONIBILI: UbicazioneMagazzino[] = [
  "presso_fornitore",
  "nostro_magazzino",
  "centro_assistenza",
];

export const TIPO_ACCESSORIO_LABELS: Record<TipoAccessorio, string> = {
  sacco_cassaforte: "Sacco cassaforte",
  cassetto_cassaforte: "Cassetto cassaforte",
  accettatore_banconote: "Accettatore banconote",
};

export interface OrdineMagazzino {
  id: string;
  odoo_order_ref: string | null;
  data_ordine: string | null;
  quantita: number;
  stato_ordine: StatoOrdineMagazzino;
  cliente_nuovo_nome: string | null;
  cliente_nuovo_piva: string | null;
  stato_match_cliente: StatoMatchCliente;
  ubicazione: UbicazioneMagazzino;
  /**
   * DOVE esattamente. Tre delle quattro ubicazioni vogliono anche il posto
   * preciso, e ognuna ha il suo campo — valorizzato solo per la sua:
   * `centro_assistenza` / `fornitore` / `sede_cliente`. `nostro_magazzino` non
   * ne ha uno: il posto è uno solo.
   */
  centro_assistenza: { id: string; nome: string } | null;
  fornitore: { id: string; nome: string } | null;
  sede_cliente: { id: string; nome_sede: string | null; citta: string | null } | null;
  note: string | null;
  dispositivo_catalogo: { id: string; marca: string; modello: string; sigla: string | null } | null;
  cliente_preassegnato: { id: string; nome: string; partita_iva: string | null } | null;
  /**
   * I pezzi fisici arrivati contro questa riga d'ordine, in ordine di arrivo.
   * Fino a `quantita`: un ordine da 5 casseforti arriva con 5 matricole, note
   * in momenti diversi. Vuoto finché non ne arriva nessuna.
   */
  unita: {
    id: string;
    created_at: string;
    device: { id: string; matricola: string | null; stato: string };
  }[];
}

export interface AccessorioMagazzino {
  id: string;
  tipo_accessorio: TipoAccessorio;
  quantita_disponibile: number;
  /** Estremi del lotto, solo per i sacchi. Non sono record singoli. */
  matricola_da: string | null;
  matricola_a: string | null;
  note: string | null;
  fornitore: { id: string; nome: string } | null;
}

export async function listOrdini(): Promise<{ records: OrdineMagazzino[] }> {
  const { data } = await apiClient.get<{ records: OrdineMagazzino[] }>("/magazzino/device");
  return data;
}

export async function listAccessori(): Promise<{ records: AccessorioMagazzino[] }> {
  const { data } = await apiClient.get<{ records: AccessorioMagazzino[] }>("/magazzino/accessori");
  return data;
}

// ---------------------------------------------------------------------------
// Fornitori — elenco chiuso per la select del form accessori.
//
// Rotta dedicata e non `/clienti-search`: quella è una ricerca a digitazione su
// tutta l'anagrafica, i fornitori sono pochi e si scelgono da un elenco.
// ---------------------------------------------------------------------------
export interface FornitoreOption {
  id: string;
  nome: string;
}

export async function listFornitori(): Promise<{ fornitori: FornitoreOption[] }> {
  const { data } = await apiClient.get<{ fornitori: FornitoreOption[] }>("/magazzino/fornitori");
  return data;
}

// ---------------------------------------------------------------------------
// Ordini
//
// `stato_match_cliente` e `cliente_preassegnato_id` non si mandano MAI dal
// form: il primo è un esito che decide il backend, il secondo lo valorizza il
// match sulla P.IVA. L'unico caso in cui il client sceglie il cliente è la
// modalità "cliente esistente", e allora `cliente_preassegnato_id` è una
// scelta umana esplicita — non un esito.
// ---------------------------------------------------------------------------
export interface OrdinePayload {
  dispositivo_catalogo_id?: string | null;
  quantita: number;
  data_ordine?: string | null;
  odoo_order_ref?: string | null;
  stato_ordine: StatoOrdineMagazzino;
  /** Modalità "cliente esistente". Mutuamente esclusivo con i due campi sotto. */
  cliente_preassegnato_id?: string | null;
  /** Modalità "cliente nuovo": non ancora in anagrafica. */
  cliente_nuovo_nome?: string | null;
  cliente_nuovo_piva?: string | null;
  ubicazione?: UbicazioneMagazzino;
  /**
   * I tre campi "dove". Da mandare solo quello dell'ubicazione richiesta: il
   * backend rifiuta gli altri se valorizzati, e azzera comunque quelli che
   * l'ubicazione non prevede.
   */
  centro_assistenza_id?: string | null;
  fornitore_id?: string | null;
  sede_id?: string | null;
  note?: string | null;
}

export async function creaOrdine(payload: OrdinePayload): Promise<{ record: OrdineMagazzino }> {
  const { data } = await apiClient.post<{ record: OrdineMagazzino }>("/magazzino/device", payload);
  return data;
}

export async function aggiornaOrdine(
  id: string,
  payload: Partial<OrdinePayload>
): Promise<{ record: OrdineMagazzino }> {
  const { data } = await apiClient.patch<{ record: OrdineMagazzino }>(`/magazzino/device/${id}`, payload);
  return data;
}

export async function eliminaOrdine(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/magazzino/device/${id}`);
  return data;
}

/**
 * La matricola è diventata nota: il backend fa trova-o-crea su `devices` e
 * aggancia l'ordine. Non è un campo del form — l'identità del pezzo vive su
 * `devices.matricola`, e questo è il momento in cui nasce.
 *
 * `creato` dice se il device è stato creato adesso o se esisteva già.
 */
export interface EsitoAssegnaMatricola {
  device: { id: string; matricola: string };
  /** true se il device è stato creato adesso, false se esisteva già. */
  creato: boolean;
  /** Quante unità ha l'ordine dopo questa, e quante ne prevede in tutto. */
  collegate: number;
  quantita: number;
}

export async function assegnaMatricola(id: string, matricola: string): Promise<EsitoAssegnaMatricola> {
  const { data } = await apiClient.post<EsitoAssegnaMatricola>(`/magazzino/device/${id}/matricola`, { matricola });
  return data;
}

/**
 * Toglie il COLLEGAMENTO fra un ordine e un pezzo, non il pezzo: quello è un
 * oggetto fisico che continua a esistere col suo storico. Serve quando una
 * matricola è stata attribuita all'ordine sbagliato.
 */
export async function scollegaUnita(ordineId: string, unitaId: string): Promise<{ success: true; matricola: string | null }> {
  const { data } = await apiClient.delete<{ success: true; matricola: string | null }>(
    `/magazzino/device/${ordineId}/unita/${unitaId}`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Accessori
// ---------------------------------------------------------------------------
export interface AccessorioPayload {
  tipo_accessorio: TipoAccessorio;
  quantita_disponibile: number;
  /** Solo per `sacco_cassaforte`. Per gli altri tipi non vanno mandati affatto. */
  matricola_da?: string | null;
  matricola_a?: string | null;
  fornitore_id?: string | null;
  note?: string | null;
}

export async function creaAccessorio(payload: AccessorioPayload): Promise<{ record: AccessorioMagazzino }> {
  const { data } = await apiClient.post<{ record: AccessorioMagazzino }>("/magazzino/accessori", payload);
  return data;
}

export async function aggiornaAccessorio(
  id: string,
  payload: Partial<AccessorioPayload>
): Promise<{ record: AccessorioMagazzino }> {
  const { data } = await apiClient.patch<{ record: AccessorioMagazzino }>(`/magazzino/accessori/${id}`, payload);
  return data;
}

export async function eliminaAccessorio(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/magazzino/accessori/${id}`);
  return data;
}

// ---------------------------------------------------------------------------
// Risoluzione manuale del match cliente
// ---------------------------------------------------------------------------
export interface ClienteCandidato {
  id: string;
  nome: string;
  partita_iva: string | null;
  codice_cliente: string | null;
}

/**
 * I clienti la cui P.IVA coincide con quella scritta sull'ordine — cioè
 * esattamente quelli che hanno reso il caso ambiguo. Lista vuota quando la
 * P.IVA non corrisponde a nessuno: lì non c'è niente da scegliere.
 */
export async function listCandidati(
  ordineId: string
): Promise<{ candidati: ClienteCandidato[]; piva_cercata: string | null; nome_scritto: string | null }> {
  const { data } = await apiClient.get<{
    candidati: ClienteCandidato[];
    piva_cercata: string | null;
    nome_scritto: string | null;
  }>(`/magazzino/device/${ordineId}/candidati`);
  return data;
}

// ---------------------------------------------------------------------------
// Disponibilità per modello e ubicazione
//
// I numeri sono quelli costruiti qui dentro, non quelli di Odoo: là la
// "Quantità a disposizione" delle casseforti non è giacenza reale e va spesso
// in negativo.
// ---------------------------------------------------------------------------
export interface RigaDisponibilita {
  dispositivo_catalogo_id: string | null;
  etichetta: string;
  sigla: string | null;
  /** Una voce per ognuna delle quattro ubicazioni, anche a zero. */
  quantita: Record<UbicazioneMagazzino, number>;
  /** Le tre ubicazioni nostre, sommate. Esclude `presso_cliente`. */
  disponibili: number;
  /** Merce uscita: `presso_cliente`. */
  fuori: number;
}

export async function getDisponibilita(): Promise<{
  righe: RigaDisponibilita[];
  totali: { disponibili: number; fuori: number };
}> {
  const { data } = await apiClient.get<{
    righe: RigaDisponibilita[];
    totali: { disponibili: number; fuori: number };
  }>("/magazzino/disponibilita");
  return data;
}

// ---------------------------------------------------------------------------
// Casseforti in magazzino
//
// Pezzi fisici, non ordini: righe di `devices` caricate dal Magazzino e non
// ancora collegate a un'installazione. Quali siano lo decide il backend
// (`utils/magazzinoCasseforti.ts`), una volta sola: qui non si filtra.
// ---------------------------------------------------------------------------
export interface CassaforteMagazzino {
  id: string;
  /** `null` = non ancora nota. */
  matricola: string | null;
  /** Arrivo presunto dal fornitore (data ISO a mezzanotte UTC); `null` = non nota. */
  data_consegna_prevista: string | null;
  caricata_in_magazzino_il: string;
  dispositivo_catalogo: { id: string; marca: string; modello: string; sigla: string | null } | null;
  /** La sede a cui è destinata (`devices.sede_id`); `null` = nessuna. */
  sede_cliente: {
    id: string;
    nome_sede: string | null;
    citta: string | null;
    cliente: { id: string; nome: string };
  } | null;
}

/** "" = tutte, "con" = già destinate a una sede, "senza" = senza sede. */
export type FiltroSedeCasseforti = "" | "con" | "senza";

export async function listCasseforti(sede: FiltroSedeCasseforti = ""): Promise<{ records: CassaforteMagazzino[] }> {
  const { data } = await apiClient.get<{ records: CassaforteMagazzino[] }>("/magazzino/casseforti", {
    params: sede ? { sede } : undefined,
  });
  return data;
}

export interface NuovaCassaforte {
  /** Marca e modello: obbligatori, dal Catalogo, di tipo Cassaforte. */
  dispositivo_catalogo_id: string;
  matricola: string | null;
  sede_id: string | null;
  data_consegna_prevista: string | null;
}

/**
 * Carica una cassaforte in magazzino. Le regole (modello obbligatorio e di tipo
 * Cassaforte, matricola univoca se c'è, sede esistente) le applica il backend:
 * il form le anticipa dove aiuta, non le riscrive.
 */
export async function creaCassaforte(payload: NuovaCassaforte): Promise<{ record: { id: string; matricola: string | null } }> {
  const { data } = await apiClient.post<{ record: { id: string; matricola: string | null } }>(
    "/magazzino/casseforti",
    payload
  );
  return data;
}
